import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import { useAuthStore } from '../../store/authStore';
import { usePlanetStore } from '../../store/planetStore';
import { usePlayerStore } from '../../store/playerStore';
import { authErrorMessage } from '../../services/authErrors';
import { SOCIAL_PROVIDER_NAMES, type SocialProvider } from '../../services/authProviders';
import PasswordAuthForm from './PasswordAuthForm';
import SocialLogin from '../SocialLogin';
import SignInMethodsLoading from './SignInMethodsLoading';
import { useSignInMethods } from '../../hooks/useSignInMethods';

export interface UpgradeAccountModalProps {
    open: boolean;
    onClose: () => void;
    zIndexClassName?: string;
}

/** Turns a guest into a real account on the same uid, so nothing in the game is lost. */
export default function UpgradeAccountModal({ open, onClose, zIndexClassName }: UpgradeAccountModalProps) {
    const { upgradeGuestWithPassword, upgradeGuestWithProvider } = useAuthStore();
    const { show } = useToast();
    const [error, setError] = useState<string | null>(null);
    const methods = useSignInMethods();

    const close = () => {
        setError(null);
        onClose();
    };

    /** Reloads the player state so the hostile-mission wait shows the time the server just stamped (issue #332). */
    const succeed = (message: string) => {
        show(message, { tone: 'success' });
        close();
        const uid = useAuthStore.getState().user?.uid;
        const planetId = usePlanetStore.getState().currentPlanet?.id;
        if (uid && planetId) void usePlayerStore.getState().loadPlayerState(uid, planetId);
    };

    const withPassword = async (email: string, password: string) => {
        setError(null);
        try {
            await upgradeGuestWithPassword(email, password);
        } catch (err) {
            setError(authErrorMessage(err));
            throw err;
        }
        succeed('Account created. Check your inbox to verify your email.');
    };

    const withProvider = async (provider: SocialProvider) => {
        setError(null);
        try {
            await upgradeGuestWithProvider(provider);
        } catch (err) {
            setError(authErrorMessage(err, SOCIAL_PROVIDER_NAMES[provider]));
            throw err;
        }
    };

    return (
        <Modal open={open} onClose={close} title="Create your account" zIndexClassName={zIndexClassName}>
            <div className="flex flex-col gap-3">
                <p className="text-body text-content-secondary">
                    Your planets, fleets and research stay exactly where they are.
                </p>
                {error && (
                    <p className="text-status-danger text-body" role="alert">
                        {error}
                    </p>
                )}
                {!methods ? (
                    <SignInMethodsLoading />
                ) : !methods.emailPassword && methods.social.length === 0 ? (
                    <p className="text-content-secondary text-body">
                        Creating an account isn&apos;t available right now. Please try again later.
                    </p>
                ) : (
                    <>
                        {methods.emailPassword && (
                            <PasswordAuthForm mode="signUp" submitLabel="Create account" onSubmit={withPassword} />
                        )}
                        <SocialLogin
                            variant="register"
                            divider={methods.emailPassword}
                            onSelect={withProvider}
                            onLoginSuccess={() => succeed('Account created. Your progress is saved.')}
                        />
                    </>
                )}
            </div>
        </Modal>
    );
}
